import { Box, Paper, Typography } from '@mui/material';
import { Formik, Form, FormikHelpers } from 'formik';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHook';
import { useEffect, useState } from 'react';
import FinancialQuestions from '@acorn-portal/shared/src/components/renewal/financial-questions';
import BankDetailsForm from './components/BankDetailsForm';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import changeBankSchema from './schemas/changeBankSchema';
import { ManagedPaymentsValues } from './types';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import { useNavigate } from 'react-router';
import API_POLICY_TOKEN from '@acorn-portal/shared/src/services/managedPaymentsInstance';
import { BankDetailsClient } from '@acorn-portal/shared/src/services/managedPaymentsApi';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import {
	getSummaryForCurrentTerm,
	getBillingSchedule,
	setCanChangeBankDetails,
} from '@acorn-portal/shared/src/store/managedPayments.slice';
import DisplayField from '@acorn-portal/shared/src/components/displayField';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import { isBillingBankAccountPaymentMethod } from '../../utils/payment-method-type-guard';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

const initialValues: ManagedPaymentsValues = {
	cardholderFirstName: '',
	cardholderEmail: '',
	cardholderLastName: '',
	saveCard: true,
	account_number: '',
	sort_code: '',
	accept_dd_terms: false,
	is_only_signatory_required: undefined,
	is_account_holder: undefined,
};

const ChangeOfBankDetails = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const policyDetails = useAppSelector((redux) => redux.fullPolicy);
	const managedPaymentsDetails = useAppSelector((redux) => redux.managedPayments);
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [isSuccessFlow, setIsSuccessFlow] = useState<boolean>(false);

	const sortCodeState =
		managedPaymentsDetails.currentBillingTerm.currentPaymentMethod &&
		isBillingBankAccountPaymentMethod(managedPaymentsDetails.currentBillingTerm.currentPaymentMethod) &&
		managedPaymentsDetails.currentBillingTerm.currentPaymentMethod.sortCode;

	const accountNumState =
		managedPaymentsDetails.currentBillingTerm.currentPaymentMethod &&
		isBillingBankAccountPaymentMethod(managedPaymentsDetails.currentBillingTerm.currentPaymentMethod) &&
		managedPaymentsDetails.currentBillingTerm.currentPaymentMethod.accountNumber;

	const accountNameState = policyDetails.drivers[0].firstName;

	const handlePaymentSubmit = async (values: ManagedPaymentsValues, helpers: FormikHelpers<ManagedPaymentsValues>) => {
		setSubmitting(true);
		try {
			const managePaymentClient = new BankDetailsClient(undefined, API_POLICY_TOKEN);
			const updatePaymentDetails = await managePaymentClient.changeBankDetails({
				sortCode: values.sort_code,
				accountNumber: values.account_number,
				accountName: accountNameState,
			});

			Promise.allSettled([dispatch(getSummaryForCurrentTerm()), dispatch(getBillingSchedule())]).then((results) =>
				results.forEach((result) => {
					if (result.status !== 'fulfilled') {
						console.error('Operation failed:', result.reason);
					}
				}),
			);

			if (updatePaymentDetails.success) {
				setIsSuccessFlow(true);
				dispatch(setCanChangeBankDetails(false));
				setSubmitting(false);
				navigate(ROUTE_PATHS.changeBankSuccess, { replace: true });
			}
		} catch (e) {
			helpers.setFieldValue('sort_code', initialValues.sort_code);
			helpers.setFieldValue('account_number', initialValues.account_number);

			helpers.setFormikState((prevState) => ({
				...prevState,

				errors: {
					...prevState.errors,
					sort_code: 'Please enter a valid sort code',
					account_number: 'Please enter a valid account number',
				},
			}));

			console.error((e as Error).message);
			setSubmitting(false);
		}
	};

	useEffect(() => {
		if (managedPaymentsDetails.paymentSchedule.canChangeBankDetails === false && !isSuccessFlow) {
			navigate(ROUTE_PATHS.changeBankError);
		}
	}, [managedPaymentsDetails, isSuccessFlow, navigate]);

	return (
		<Paper
			elevation={1}
			sx={{
				borderRadius: '8px',
			}}
		>
			<JourneyCardLayout pageTitle='Change Bank Details' loading={false}>
				<Box
					sx={{
						pb: 4,
						margin: 2,
						textAlign: 'left',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<>
						{sortCodeState && accountNumState && (
							<Box
								sx={(theme) => ({
									border: `1px solid ${theme.palette.divider}`,
									p: 2,
									width: '100%',
									mb: 4,
									maxWidth: '700px',
									borderRadius: 2,
								})}
							>
								<Box display='flex'>
									<Typography variant='h6' color='secondary' sx={{ mb: 2 }}>
										Current Bank Details
									</Typography>
								</Box>
								<DisplayField label='Bank Account Number'>{accountNumState}</DisplayField>
								<DisplayField label='Sort Code'>{sortCodeState}</DisplayField>
							</Box>
						)}
					</>
					<Formik initialValues={initialValues} onSubmit={handlePaymentSubmit} validationSchema={changeBankSchema}>
						{(formik) => {
							return (
								<>
									<Box sx={{ display: 'flex' }}>
										<Form>
											<FormikLogErrors />
											<Box sx={{ textAlign: { xs: 'left', md: 'unset' } }}>
												<FinancialQuestions formik={formik} />
												<BankDetailsForm formik={formik} />
											</Box>
											<ActionButtons>
												<BackButton onClick={() => navigate(-1)}>Cancel</BackButton>
												<ContinueButton type='submit'>Submit</ContinueButton>
											</ActionButtons>
										</Form>
									</Box>
									<LoadingModal open={submitting} message='Updating Bank Details' />
								</>
							);
						}}
					</Formik>
				</Box>
			</JourneyCardLayout>
		</Paper>
	);
};

export default ChangeOfBankDetails;
